<template>
  <div class="public-application-form">
    <v-card v-if="notFound" class="public-application-form__notice" flat>
      <v-icon color="warning" size="36">mdi-link-variant-off</v-icon>
      <h2>This application link has expired</h2>
      <p>Please contact the organization for a new link.</p>
    </v-card>

    <div v-if="!infoCollected">
      <section v-if="showType && leadType === null" class="application-type-picker">
        <div class="application-type-picker__heading">
          <div class="application-type-picker__icon">
            <v-icon color="primary">mdi-account-check-outline</v-icon>
          </div>
          <div>
            <span>Before you begin</span>
            <h2>Who is this application for?</h2>
            <p>Choose an option to see the right enrollment form.</p>
          </div>
        </div>

        <div class="application-type-picker__options">
          <button
            type="button"
            class="application-type-option"
            :class="{ 'application-type-option--selected': leadType === 0 }"
            @click="leadType = 0"
          >
            <span class="application-type-option__icon">
              <v-icon color="primary" size="28">mdi-account-child-outline</v-icon>
            </span>
            <span class="application-type-option__copy">
              <strong>My child</strong>
              <small>Parent or guardian completing an application for one or more children.</small>
            </span>
            <v-icon color="primary">mdi-chevron-right</v-icon>
          </button>

          <button
            type="button"
            class="application-type-option"
            :class="{ 'application-type-option--selected': leadType === 1 }"
            @click="leadType = 1"
          >
            <span class="application-type-option__icon">
              <v-icon color="primary" size="28">mdi-account-outline</v-icon>
            </span>
            <span class="application-type-option__copy">
              <strong>Myself</strong>
              <small>Adult applicant enrolling in a class or program.</small>
            </span>
            <v-icon color="primary">mdi-chevron-right</v-icon>
          </button>
        </div>

        <div class="application-type-picker__privacy">
          <v-icon size="17" color="primary">mdi-shield-lock-outline</v-icon>
          Your information is shared only with this organization.
        </div>
      </section>


      <v-container class="application-flow pa-0" fluid v-if="leadType === 0">

        <v-layout align-center justify-center>
          <v-flex xs12>
            <v-card v-if="!infoCollected" class="application-flow__card" outlined>
              <div class="application-flow__intro">
                <div>
                  <span>Child enrollment</span>
                  <h2>Start with your student</h2>
                  <p>Add one or more students, then tell us how to contact your family.</p>
                </div>
                <div class="application-flow__step-count">Step {{ step }} of 4</div>
              </div>
              <v-stepper vertical v-model="step" class="application-stepper elevation-0">
                <v-stepper-step :complete="step > 1" step="1">
                  Student information
                </v-stepper-step>
                <v-stepper-content step="1">
                  <v-card-text>
                    <div v-if="Students.length" class="application-student-list">
                      <div v-for="(student, index) in Students" :key="`${student.firstName}-${index}`" class="application-student-list__item">
                        <div>
                          <strong>{{ student.firstName }} {{ student.lastName }}</strong>
                          <span>{{ student.selectedClass.name }} · {{ student.dob | moment('L') }}</span>
                        </div>
                        <v-btn icon :aria-label="`Remove ${student.firstName}`" @click="deleteItem(student)">
                          <v-icon>mdi-delete-outline</v-icon>
                        </v-btn>
                      </div>
                    </div>
                    <div class="application-student-editor">
                      <div class="application-student-editor__heading">
                        <strong>{{ Students.length ? 'Add another student' : 'First student' }}</strong>
                        <span>Enter the child’s details and choose a department.</span>
                      </div>
                      <v-row class="mt-2">
                        <v-col cols="12" sm="6">
                          <v-text-field autofocus outlined dense :background-color="`${Student.firstName ? false : '#E8F5E9'}`"
                            :error-messages="studentFirstNameErrors" :hide-details="studentFirstNameErrors.length === 0"
                            v-model="Student.firstName" label="First Name"></v-text-field>
                        </v-col>
                        <v-col cols="12" sm="6">
                          <v-text-field outlined dense :background-color="`${Student.lastName ? false : '#E8F5E9'}`"
                            :error-messages="studentLastNameErrors" :hide-details="studentLastNameErrors.length === 0"
                            v-model="Student.lastName" label="Last Name"></v-text-field>
                        </v-col>
                        <v-col cols="12" sm="6">
                          <v-select dense outlined :items="['male', 'female']" label="Gender" v-model="Student.gender"
                            :background-color="`${Student.gender ? false : '#E8F5E9'}`" :error-messages="genderErrors"
                            :hide-details="genderErrors.length === 0"></v-select>
                        </v-col>
                        <v-col cols="12" sm="6">
                          <v-text-field outlined dense type="date" label="Date of Birth" v-model="Student.dob"
                            :background-color="`${Student.dob ? false : '#E8F5E9'}`" :error-messages="dobError"
                            :hide-details="dobError.length === 0"></v-text-field>
                        </v-col>
                        <v-col cols="12">
                          <v-autocomplete @change="classChange" dense outlined clearable return-object item-text="name"
                            :items="filterByIsChild(Classes, true)" v-model="Student.selectedClass" label="Select Department"
                            :background-color="`${Student.selectedClass ? false : '#E8F5E9'}`" :error-messages="classIdErrors"
                            :hide-details="classIdErrors.length === 0">
                            <template slot="item" slot-scope="{ item }">
                              <span>{{ item.name }}</span><v-spacer></v-spacer><span>{{ item.defaultFee | currency }}</span>
                            </template>
                          </v-autocomplete>
                        </v-col>
                      </v-row>
                      <div v-if="Student.metadata" class="application-student-questions">
                        <div v-for="(element, idx) in Student.metadata" :key="idx" class="application-student-questions__item">
                          <label>{{ element.question }} <span v-if="element.isRequired" class="error--text">*</span></label>
                          <v-text-field v-if="element.selectedAnswerType === 'Short answer'" v-model="element.answer"
                            outlined dense hide-details></v-text-field>
                          <v-textarea v-else-if="element.selectedAnswerType === 'Paragraph'" v-model="element.answer"
                            outlined dense hide-details></v-textarea>
                          <div v-else-if="element.selectedAnswerType === 'Checkboxes'">
                            <v-checkbox v-for="(choice, choiceIndex) in element.ChoiceList" :key="choiceIndex"
                              v-model="choice.answer" :label="choice.label" hide-details class="mt-1"></v-checkbox>
                          </div>
                          <v-radio-group v-else-if="element.selectedAnswerType === 'Multiple choice'" v-model="element.answer" hide-details>
                            <v-radio v-for="(choice, choiceIndex) in element.ChoiceList" :key="choiceIndex"
                              :label="choice.label" :value="choice.answer"></v-radio>
                          </v-radio-group>
                          <v-select v-else-if="element.selectedAnswerType === 'Dropdown'" v-model="element.answer"
                            :items="element.ChoiceList" item-text="label" outlined dense hide-details></v-select>
                        </div>
                      </div>
                      <div v-if="Student.dob && getAge(Student.dob) < 5" class="error--text application-student-editor__age">
                        Minimum admission age is 5 years.
                      </div>
                    </div>
                  </v-card-text>
                  <v-divider></v-divider>
                  <v-card-actions>
                    <v-btn v-if="hasStudentDraft" outlined color="primary" large @click="addStudent">
                      <v-icon left small>mdi-account-plus-outline</v-icon>Add another student
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn @click="studentsDone" color="primary" large>Next</v-btn>
                  </v-card-actions>
                </v-stepper-content>

                <v-stepper-step :complete="step > 2" step="2">
                  Address
                </v-stepper-step>
                <v-stepper-content step="2">
                  <v-card-text>
                    <v-row class="mt-2">
                      <v-col cols="12" sm="12">
                        <v-text-field outlined :error-messages="addressErrors"
                          :background-color="`${Address.address ? false : '#E8F5E9'}`"
                          :hide-details="addressErrors.length === 0" dense v-model="Address.address" label="Address">
                        </v-text-field>
                      </v-col>
                      <v-col cols="12" sm="12" md="4">
                        <v-text-field outlined :error-messages="cityErrors" :hide-details="cityErrors.length === 0"
                          :background-color="`${Address.city ? false : '#E8F5E9'}`" dense
                          @change="Address.city = properCase(Address.city)" v-model="Address.city" label="City">
                        </v-text-field>
                      </v-col>
                      <v-col cols="12" sm="12" md="4">
                        <v-text-field outlined dense v-mask="'AA'" :error-messages="stateErrors"
                          :background-color="`${Address.state ? false : '#E8F5E9'}`"
                          :hide-details="stateErrors.length === 0" v-model="Address.state" label="State">
                        </v-text-field>
                      </v-col>
                      <v-col cols="12" sm="12" md="4">
                        <v-text-field outlined dense v-mask="'#####'" :error-messages="zipErrors"
                          :background-color="`${Address.zip ? false : '#E8F5E9'}`"
                          :hide-details="zipErrors.length === 0" v-model="Address.zip" label="Zip Code">
                        </v-text-field>
                      </v-col>
                    </v-row>
                  </v-card-text>
                  <v-divider></v-divider>
                  <v-card-actions>
                    <v-btn large text @click="step = step - 1">Back</v-btn>
                    <v-spacer></v-spacer>
                    <v-btn @click="addressDone" color="primary" large>Next</v-btn>
                  </v-card-actions>
                </v-stepper-content>

                <v-stepper-step :complete="step > 3" step="3">
                  Parent or guardian information
                </v-stepper-step>
                <v-stepper-content step="3">
                  <v-card-text>
                    <p class="application-section-help">Now tell us who we should contact about the student{{ Students.length === 1 ? '' : 's' }}.</p>
                    <v-row class="mt-2">
                      <v-col cols="12" md="6">
                        <v-text-field autofocus outlined dense :error-messages="fatherNameErrors"
                          :background-color="`${Lead.fatherName ? false : '#E8F5E9'}`" :hide-details="fatherNameErrors.length === 0"
                          v-model="Lead.fatherName" label="Parent or Guardian Name"></v-text-field>
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-text-field type="tel" outlined dense v-mask="'(###) ###-####'"
                          :background-color="`${Lead.fatherPhoneNumber ? false : '#E8F5E9'}`" :error-messages="fatherPhoneNumberErrors"
                          :hide-details="fatherPhoneNumberErrors.length === 0" v-model="Lead.fatherPhoneNumber"
                          label="Primary Phone Number"></v-text-field>
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-text-field outlined dense :error-messages="fatherEmailAddressErrors"
                          :background-color="`${Lead.fatherEmailAddress ? false : '#E8F5E9'}`" :hide-details="fatherEmailAddressErrors.length === 0"
                          @change="fixFatherEmail" v-model="Lead.fatherEmailAddress" label="Email Address"></v-text-field>
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-text-field outlined dense hide-details v-model="Lead.motherName" label="Second Parent or Guardian Name (optional)"></v-text-field>
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-text-field outlined dense hide-details v-mask="'(###) ###-####'" type="tel"
                          v-model="Lead.motherPhoneNumber" label="Second Parent Phone (optional)"></v-text-field>
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-text-field outlined dense :error-messages="motherEmailAddressErrors"
                          :hide-details="motherEmailAddressErrors.length === 0" @change="fixMotherEmail"
                          v-model="Lead.motherEmailAddress" label="Second Parent Email (optional)"></v-text-field>
                      </v-col>
                    </v-row>
                  </v-card-text>
                  <v-divider></v-divider>
                  <v-card-actions>
                    <v-btn large text @click="step = step - 1">Back</v-btn>
                    <v-spacer></v-spacer>
                    <v-btn @click="nameDone" color="primary" large>Next</v-btn>
                  </v-card-actions>
                </v-stepper-content>

                <v-stepper-step :complete="step > 4" step="4">
                  Emergency contact
                </v-stepper-step>

                <v-stepper-content step="4">
                  <v-card-text>
                    <p class="application-section-help">Who should we contact if a parent or guardian cannot be reached?</p>
                    <v-row class="mt-2">
                      <v-col cols="12" md="4">
                        <v-text-field outlined dense :error-messages="emergencyNameErrors"
                          :background-color="`${Emergency.emergencyName ? false : '#E8F5E9'}`" :hide-details="emergencyNameErrors.length === 0"
                          v-model="Emergency.emergencyName" label="Contact Name"></v-text-field>
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-text-field outlined dense v-mask="'(###) ###-####'" type="tel"
                          :background-color="`${Emergency.emergencyPhoneNumber ? false : '#E8F5E9'}`" :error-messages="emergencyPhoneNumberErrors"
                          :hide-details="emergencyPhoneNumberErrors.length === 0" v-model="Emergency.emergencyPhoneNumber"
                          label="Phone Number"></v-text-field>
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-text-field outlined dense :error-messages="emergencyRelationErrors"
                          :background-color="`${Emergency.emergencyRelation ? false : '#E8F5E9'}`" :hide-details="emergencyRelationErrors.length === 0"
                          v-model="Emergency.emergencyRelation" label="Relationship"></v-text-field>
                      </v-col>
                    </v-row>
                  </v-card-text>
                  <v-divider></v-divider>
                  <v-card-actions>
                    <v-btn large text @click="step = step - 1">Back</v-btn>
                    <v-spacer></v-spacer>
                    <!-- <v-btn @click="studentDone" v-if="takeCard" color="primary" large>next</v-btn> -->
                    <v-btn @click="emergencyDone" :loading="loading" color="primary" large>
                      Submit application
                    </v-btn>
                  </v-card-actions>
                </v-stepper-content>

                <!-- <v-stepper-step :complete="step > 5" step="5" v-if="takeCard">
                  Confirmation
                </v-stepper-step>

                <v-stepper-content step="5" v-if="takeCard" class="pl-0 pr-10 ml-2">
                  <div class="my-3 text-center">
                    Your card will not be charge until student start the
                    classes
                  </div>
                  <v-card v-if="StripeKey" max-width="400" class="centerdiv" flat>
                    <v-card outlined>
                      <v-card-text>
                        <card ref="card" class="stripe-card" :class="{ complete }" :stripe="StripeKey"
                          @change="complete = $event.complete" />
                      </v-card-text>
                    </v-card>
                    <v-img class="text-center" src="@/assets/images/stripeimg.jpg" />
                  </v-card>
                  <div v-if="Business" class="text-center mt-5">
                    By pressing submit button you have agree to all
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on }">
                        <a target="_blank" @click="openTermBox = true" v-on="on">
                          terms and conditions
                        </a>
                      </template>
                      Opens in new window
                    </v-tooltip>
                    of {{ Business.name }}
                  </div>
                  <v-divider></v-divider>
                  <v-card-actions>
                    <v-btn large text @click="step = step - 1">Back</v-btn>
                    <v-spacer></v-spacer>
                    <v-btn @click="submit" :loading="loading" color="primary" large>Submit</v-btn>
                  </v-card-actions>
                </v-stepper-content> -->
              </v-stepper>
            </v-card>
          </v-flex>
        </v-layout>
      </v-container>

      <v-container class="application-flow pa-0" fluid v-if="leadType === 1">
        <v-layout align-center justify-center>
          <v-flex xs12>
            <v-card v-if="!infoCollected" class="application-flow__card" outlined>
              <div class="application-flow__intro">
                <div>
                  <span>Adult enrollment</span>
                  <h2>Tell us about yourself</h2>
                  <p>Complete your information and select a class.</p>
                </div>
                <div class="application-flow__step-count">Step {{ adultStep }} of 2</div>
              </div>
              <v-stepper vertical v-model="adultStep" class="application-stepper elevation-0">
                <v-stepper-step :complete="adultStep > 1" step="1">
                  Your Information
                </v-stepper-step>
                <v-stepper-content step="1">
                  <v-card-text>
                    <v-row>
                      <v-col cols="12" sm="6">
                        <v-text-field outlined dense :background-color="`${Student.firstName ? false : '#E8F5E9'}`"
                          :error-messages="studentFirstNameErrors" :hide-details="studentFirstNameErrors.length === 0" 
                          autofocus v-model="Student.firstName" label="First Name"></v-text-field>
                      </v-col>
                      <v-col cols="12" sm="6">
                        <v-text-field outlined dense style="display: none"></v-text-field>
                        <v-text-field outlined :background-color="`${Student.lastName ? false : '#E8F5E9'}`"
                          :error-messages="studentLastNameErrors" :hide-details="studentLastNameErrors.length === 0" 
                          dense v-model="Student.lastName" label="Last Name"></v-text-field>
                      </v-col>
                      <v-col cols="12" sm="12" md="6">
                        <v-text-field type="tel" outlined dense v-mask="'(###) ###-####'"
                          :background-color="`${Lead.fatherPhoneNumber ? false : '#E8F5E9'}`"
                          :error-messages="fatherPhoneNumberErrors" :hide-details="fatherPhoneNumberErrors.length === 0"
                          v-model="Lead.fatherPhoneNumber" label="Phone Number"></v-text-field>
                      </v-col>
                      <v-col cols="12" sm="12" md="6">
                        <v-text-field outlined dense :error-messages="fatherEmailAddressErrors"
                          :background-color="`${Lead.fatherEmailAddress ? false : '#E8F5E9'}`" :hide-details="fatherEmailAddressErrors.length === 0
                            " @change="fixFatherEmail" v-model="Lead.fatherEmailAddress" label="Email">
                        </v-text-field>
                      </v-col>
                      <v-col cols="12" sm="12" md="6">
                        <v-select dense :background-color="`${Student.gender ? false : '#E8F5E9'}`"
                          :error-messages="genderErrors" :hide-details="genderErrors.length === 0"
                          :items="['male', 'female']" label="Gender" v-model="Student.gender" outlined>
                        </v-select>
                      </v-col>
                      <v-col cols="12" sm="12" md="6">
                        <v-text-field outlined dense :background-color="`${Student.dob ? false : '#E8F5E9'}`"
                          :error-messages="dobError" :hide-details="dobError.length === 0" type="date"
                          v-model="Student.dob" label="Date of Birth"></v-text-field>
                      </v-col>
                      <v-col cols="12" sm="12">
                        <v-autocomplete @change="classChange" dense :items="filterByIsChild(Classes, false)"
                          :background-color="`${Student.selectedClass ? false : '#E8F5E9'}`" clearable item-text="name"
                          :error-messages="classIdErrors" :hide-details="classIdErrors.length === 0"
                          v-model="Student.selectedClass" return-object label="Select Class" outlined>
                          <template slot="item" slot-scope="{ item }">
                            <span>{{ item.name }}</span>
                            <v-spacer></v-spacer>
                            <span>{{
                              item.defaultFee | currency
                              }}</span>
                          </template>
                        </v-autocomplete>
                      </v-col>


                    </v-row>
                  </v-card-text>
                  <v-divider></v-divider>
                  <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn @click="adultNameDone" color="primary" large="large">Next</v-btn>
                  </v-card-actions>
                </v-stepper-content>

                <v-stepper-step :complete="adultStep > 2" step="2">
                  Address
                </v-stepper-step>
                <v-stepper-content step="2">
                  <v-card-text>
                    <v-row class="mt-2">
                      <v-col cols="12" sm="12">
                        <v-text-field outlined :error-messages="addressErrors"
                          :background-color="`${Address.address ? false : '#E8F5E9'}`"
                          :hide-details="addressErrors.length === 0" dense v-model="Address.address" label="Address">
                        </v-text-field>
                      </v-col>
                      <v-col cols="12" sm="12" md="4">
                        <v-text-field outlined :error-messages="cityErrors" :hide-details="cityErrors.length === 0"
                          :background-color="`${Address.city ? false : '#E8F5E9'}`" dense
                          @change="Address.city = properCase(Address.city)" v-model="Address.city" label="City">
                        </v-text-field>
                      </v-col>
                      <v-col cols="12" sm="12" md="4">
                        <v-text-field outlined dense v-mask="'AA'" :error-messages="stateErrors"
                          :background-color="`${Address.state ? false : '#E8F5E9'}`"
                          :hide-details="stateErrors.length === 0" v-model="Address.state" label="State">
                        </v-text-field>
                      </v-col>
                      <v-col cols="12" sm="12" md="4">
                        <v-text-field outlined dense v-mask="'#####'" :error-messages="zipErrors"
                          :background-color="`${Address.zip ? false : '#E8F5E9'}`"
                          :hide-details="zipErrors.length === 0" v-model="Address.zip" label="Zip Code">
                        </v-text-field>
                      </v-col>
                    </v-row>
                  </v-card-text>
                  <v-divider></v-divider>
                  <v-card-actions>
                    <v-btn large text @click="adultStep = adultStep - 1">Back</v-btn>
                    <v-spacer></v-spacer>
                    <v-btn @click="adultAddressDone" :loading="loading" color="primary" large>Submit application</v-btn>
                  </v-card-actions>
                </v-stepper-content>


                
              </v-stepper>
            </v-card>
          </v-flex>
        </v-layout>
      </v-container>
    </div>

    <div v-else class="application-success" role="status">
      <v-card-text>
        <lottie :height="200" :options="defaultOptions" name="receiptCheck" v-on:animCreated="handleAnimation" />
        <div class="application-success__copy">
          <span>Application received</span>
          <h2>You’re all set</h2>
          <p>Thank you for applying. The organization will review your information and contact you soon, insha’Allah.</p>
          <div v-if="submittedApplicationNumber" class="application-success__number">
            <span>Application number</span>
            <strong>#{{ submittedApplicationNumber }}</strong>
          </div>
          <p v-if="Business && (Business.phoneNumber || Business.email)" class="application-success__contact">
            Questions? Contact {{ Business.name }}<template v-if="Business.phoneNumber"> at {{ Business.phoneNumber | USPhoneNumber }}</template><template v-if="Business.email"> or {{ Business.email }}</template>.
          </p>
        </div>
      </v-card-text>
    </div>

    <v-dialog v-model="dialog" persistent scrollable max-width="560px">
      <v-card class="application-student-dialog">
        <div class="application-student-dialog__header">
          <div>
            <span>Child enrollment</span>
            <h2>Add a student</h2>
            <p>Enter the child’s details and choose a department.</p>
          </div>
          <v-btn icon aria-label="Close add student dialog" @click.stop="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-divider />

        <v-card-text class="application-student-dialog__body">
          <v-row>
            <v-col cols="12" sm="6">
              <v-text-field outlined dense :background-color="`${Student.firstName ? false : '#E8F5E9'}`"
                :error-messages="studentFirstNameErrors" :hide-details="studentFirstNameErrors.length === 0
                  " autofocus v-model="Student.firstName" label="First Name"></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field outlined :background-color="`${Student.lastName ? false : '#E8F5E9'}`"
                :error-messages="studentLastNameErrors" :hide-details="studentLastNameErrors.length === 0
                  " dense v-model="Student.lastName" label="Last Name"></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-select dense :background-color="`${Student.gender ? false : '#E8F5E9'}`" :error-messages="genderErrors"
                :hide-details="genderErrors.length === 0" :items="['male', 'female']" label="Gender"
                v-model="Student.gender" outlined>
              </v-select>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field outlined dense :background-color="`${Student.dob ? false : '#E8F5E9'}`"
                :error-messages="dobError" :hide-details="dobError.length === 0" type="date" v-model="Student.dob"
                label="Date of Birth"></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-autocomplete @change="classChange" dense :items="filterByIsChild(Classes, true)"
                :background-color="`${Student.selectedClass ? false : '#E8F5E9'}`" clearable item-text="name"
                :error-messages="classIdErrors" :hide-details="classIdErrors.length === 0"
                v-model="Student.selectedClass" return-object label="Select Department" outlined>
                <template slot="item" slot-scope="{ item }">
                  <span>{{ item.name }}</span>
                  <v-spacer></v-spacer>
                  <span>{{
                    item.defaultFee | currency
                    }}</span>
                </template>
              </v-autocomplete>
            </v-col>
            <v-col v-if="Student.metadata && Student.metadata">
              <v-layout column>
                <v-flex xs12 v-for="(element, idx) in Student.metadata" :key="idx">
                  <v-card outlined class="mb-3">
                    <v-card-text>
                      <v-row>
                        <v-col cols="12">
                          <h2 class="text-capitalize subtitle-2 black--text">{{ element.question }} <span
                              class="red--text" v-if="element.isRequired">*</span></h2>
                        </v-col>
                        <v-col cols="12">
                          <v-text-field v-if="element.selectedAnswerType === 'Short answer'" v-model="element.answer"
                            hide-details outlined dense></v-text-field>
                          <v-textarea v-model="element.answer" v-if="element.selectedAnswerType === 'Paragraph'"
                            outlined hide-details dense placeholder="Long answer text"></v-textarea>
                          <div v-if="element.selectedAnswerType == 'Checkboxes'">
                            <v-row v-for="(list, ind) in element.ChoiceList" :key="ind">
                              <v-col cols="12" v-if="element.selectedAnswerType === 'Checkboxes'">
                                <v-checkbox v-model="list.answer" :key="ind" :label="list.label" hide-details
                                  class="mt-0 shrink mx-0"></v-checkbox>
                              </v-col>

                            </v-row>
                          </div>
                          <div v-if="element.selectedAnswerType == 'Multiple choice'">
                            <v-row>
                              <v-col cols="12">
                                <v-radio-group v-model="element.answer">
                                  <v-radio v-for="(list, ind) in element.ChoiceList" v-model="list.answer" :key="ind"
                                    :label="list.label" hide-details class="mt-0 shrink mx-0"
                                    :value="list.answer"></v-radio>
                                </v-radio-group>
                                <!-- <v-radio v-model="list.answer" :key="ind" :label="list.label"
                                                   hide-details class="mt-0 shrink mx-0"></v-radio> -->
                              </v-col>
                            </v-row>
                          </div>
                          <div v-if="element.selectedAnswerType === 'Dropdown'">
                            <v-row>
                              <v-col cols="12">
                                <v-select item-text="label" v-model="element.answer" hide-details
                                  :items="element.ChoiceList" dense outlined></v-select>
                              </v-col>
                            </v-row>
                          </div>
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </v-flex>
              </v-layout>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-text class="application-student-dialog__age">
          <div>
            {{ Student.firstName }} {{ Student.lastName }}
            <span v-if="Student.dob">is {{ getAge(Student.dob) }} year old</span>
          </div>
          <div v-if="Student.dob && getAge(Student.dob) < 5" class="error--text">
            Minimum admission age is 5 years.
          </div>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="application-student-dialog__actions">
          <v-btn text @click="closeDialog">Cancel</v-btn>
          <v-spacer />
          <v-btn color="primary" depressed :disabled="!Student.dob || getAge(Student.dob) < 5" @click="addStudent">
            <v-icon left small>mdi-account-plus-outline</v-icon>
            Add student
          </v-btn>
        </v-card-actions>
        <!-- <div v-if="Student.selectedClass && Student.selectedClass.metadata">{{
                           Student.selectedClass.metadata.customValue }}
                         </div> -->
      </v-card>
    </v-dialog>


    <v-dialog v-model="openTermBox">
      <v-card v-if="Business">
        <ModelTitle title="Terms & Conditions" @close="openTermBox = false" />

        <v-card-text>
          <v-textarea hide-details readonly auto-grow v-model="Business.policy"></v-textarea>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="openTermBox = false">
            close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- <v-card-actions>
      <div class="text-center">
        Power by: <span>taleem.app</span> -
        <span class="caption">{{ version }}</span>
      </div>
    </v-card-actions> -->
    <footer class="application-footer">
      <v-icon size="14">mdi-shield-check-outline</v-icon>
      Secure application powered by <strong>taleem.app</strong>
      <span>v{{ version }}</span>
    </footer>
  </div>
</template>

<script>
import applicationService from "@/modules/Application/service.js";
// import { Card, createToken } from "vue-stripe-elements-plus";
const { validationMixin } = require("vuelidate");
const { required, email } = require("vuelidate/lib/validators");
import Lottie from "@/components/lottie.vue";
import GlobalFunction from "@/mixins/GlobalFunction";
import * as animationData from "@/assets/lottie/1708-success.json";
const pjson = require("../../../../package.json");


export default {
  name: "application-page",
  props: ['currentBusiness', 'takeCard'],
  data() {
    return {
      showType: true,
      isChild: true,
      leadType: null,
      step: 1,
      adultStep: 1,
      infoCollected: false,
      submittedApplicationNumber: null,
      openTermBox: false,
      loading: false,
      Business: null,
      StripeKey: null,
      notFound: false,
      dialog: false,
      complete: false,
      defaultOptions: {
        animationData: animationData.default,
        loop: false,
        autoplay: true,
      },
      headers: [
        {
          text: "Name",
          align: "start",
          sortable: false,
          value: "firstName",
        },
        { text: "DOB", value: "dob", sortable: false },
        { text: "Class", value: "selectedClass.name", sortable: false },
        { text: "Action", value: "actions", sortable: false },
      ],
      Lead: {
        fatherName: null,
        fatherPhoneNumber: null,
        fatherEmailAddress: null,
        motherName: null,
        motherPhoneNumber: null,
        motherEmailAddress: null,
      },
      Address: {
        address: null,
        city: null,
        state: "NY",
        zip: null,
      },
      Emergency: {
        emergencyName: null,
        emergencyPhoneNumber: null,
        emergencyRelation: null,
      },
      Student: {
        firstName: null,
        lastName: null,
        dob: null,
        selectedClass: null,
        ClassId: null,
      },
      Students: [],
      Classes: [],
      selectedClass: null,
    };
  },
  mixins: [validationMixin, GlobalFunction],
  validations: {
    Lead: {
      fatherName: {
        required,
      },
      fatherPhoneNumber: {
        required,
      },
      fatherEmailAddress: {
        required,
        email,
      },
      motherEmailAddress: {
        email,
      },
    },
    Emergency: {
      emergencyName: {
        required,
      },
      emergencyPhoneNumber: {
        required,
      },
      emergencyRelation: {
        required,
      },
    },
    Address: {
      address: {
        required,
      },
      city: {
        required,
      },
      state: {
        required,
      },
      zip: {
        required,
      },
    },
    Student: {
      firstName: {
        required,
      },
      lastName: {
        required,
      },
      dob: {
        required,
      },
      gender: {
        required,
      },
      selectedClass: {
        required,
      },
    },
  },
  computed: {
    hasStudentDraft() {
      return Boolean(this.Student.firstName || this.Student.lastName || this.Student.dob || this.Student.gender || this.Student.selectedClass);
    },
    version() {
      return pjson.version;
    },
    studentFirstNameErrors() {
      const errors = [];
      if (!this.$v.Student.firstName.$dirty) return errors;
      if (!this.$v.Student.firstName.required)
        errors.push("First Name is required");
      return errors;
    },
    studentLastNameErrors() {
      const errors = [];
      if (!this.$v.Student.lastName.$dirty) return errors;
      if (!this.$v.Student.lastName.required)
        errors.push("Last Name is required");
      return errors;
    },
    dobError() {
      const errors = [];
      if (!this.$v.Student.dob.$dirty) return errors;
      if (!this.$v.Student.dob.required)
        errors.push("Date of birth is required");
      return errors;
    },
    classIdErrors() {
      const errors = [];
      if (!this.$v.Student.selectedClass.$dirty) return errors;
      if (!this.$v.Student.selectedClass.required)
        errors.push("Class is required");
      // if (!this.$v.Product.selectedClass.minValue) errors.push("Please Select Department");

      return errors;
    },
    genderErrors() {
      const errors = [];
      if (!this.$v.Student.gender.$dirty) return errors;
      if (!this.$v.Student.gender.required) errors.push("Gender is required");

      return errors;
    },
    fatherNameErrors() {
      const errors = [];
      if (!this.$v.Lead.fatherName.$dirty) return errors;
      if (!this.$v.Lead.fatherName.required) errors.push("Name is required");
      return errors;
    },
    fatherPhoneNumberErrors() {
      const errors = [];
      if (!this.$v.Lead.fatherPhoneNumber.$dirty) return errors;
      if (!this.$v.Lead.fatherPhoneNumber.required)
        errors.push("Phone Number is required");
      return errors;
    },
    fatherEmailAddressErrors() {
      const errors = [];
      if (!this.$v.Lead.fatherEmailAddress.$dirty) return errors;
      if (!this.$v.Lead.fatherEmailAddress.required)
        errors.push("Email Address is required");
      if (!this.$v.Lead.fatherEmailAddress.email)
        errors.push("Email is not valid");
      return errors;
    },
    motherEmailAddressErrors() {
      const errors = [];
      if (!this.$v.Lead.motherEmailAddress.$dirty) return errors;
      if (!this.$v.Lead.motherEmailAddress.email)
        errors.push("Email is not valid");
      return errors;
    },
    addressErrors() {
      const errors = [];
      if (!this.$v.Address.address.$dirty) return errors;
      if (!this.$v.Address.address.required) errors.push("Address is required");
      return errors;
    },
    cityErrors() {
      const errors = [];
      if (!this.$v.Address.city.$dirty) return errors;
      if (!this.$v.Address.city.required) errors.push("City is required");
      return errors;
    },
    stateErrors() {
      const errors = [];
      if (!this.$v.Address.state.$dirty) return errors;
      if (!this.$v.Address.state.required) errors.push("State is required");
      return errors;
    },
    zipErrors() {
      const errors = [];
      if (!this.$v.Address.zip.$dirty) return errors;
      if (!this.$v.Address.zip.required) errors.push("Zip Code is required");
      return errors;
    },
    emergencyNameErrors() {
      const errors = [];
      if (!this.$v.Emergency.emergencyName.$dirty) return errors;
      if (!this.$v.Emergency.emergencyName.required)
        errors.push("Name is required");
      return errors;
    },
    emergencyPhoneNumberErrors() {
      const errors = [];
      if (!this.$v.Emergency.emergencyPhoneNumber.$dirty) return errors;
      if (!this.$v.Emergency.emergencyPhoneNumber.required)
        errors.push("Phone Number is required");
      return errors;
    },
    emergencyRelationErrors() {
      const errors = [];
      if (!this.$v.Emergency.emergencyRelation.$dirty) return errors;
      if (!this.$v.Emergency.emergencyRelation.required)
        errors.push("Relation is required");
      return errors;
    },
  },
  components: {
    Lottie,
  },
  created() {
    // let a = "(718) 299-2222";

    // let st = a.replace(/[^a-zA-Z0-9]/g, "");

    // console.log("test test", st);
    // this.loadBusiness();
    this.Business = this.currentBusiness
    this.StripeKey = this.currentBusiness && this.currentBusiness.metadata
      ? this.currentBusiness.metadata.publicStripeId
      : null
    this.Classes = this.currentBusiness && this.currentBusiness.Classes
      ? this.currentBusiness.Classes
      : []
    this.showTypeQuery = String(this.$route.query.type || this.$route.query.for || '').toLowerCase()
    console.log('this.showType', this.showType, this.showTypeQuery)

    if (['adult', 'self', 'myself'].includes(this.showTypeQuery)) {
      this.leadType = 1
      this.showType = false
    } else if (['kids', 'kid', 'child', 'children'].includes(this.showTypeQuery)) {
      this.leadType = 0
      this.showType = false
    } else {
      this.leadType = null
      this.showType = true
    }
  },
  watch: {
    leadType() {
      this.step = 1
      this.adultStep = 1
      this.emptyDialog()
      this.emptyFrom()
    },
    currentBusiness(val) {
      this.StripeKey = val && val.metadata ? val.metadata.publicStripeId : null;
      this.Classes = val && val.Classes ? val.Classes : [];
    }
  },
  methods: {
    filterByIsChild(array, isChildValue) {
      return array.filter(item => item.isChild === isChildValue);
    },
    classChange() {
      if (this.Student.selectedClass && this.Student.selectedClass.metadata && this.Student.selectedClass.metadata.customValue) {
        // console.log('this.Student.selectedClass.metadata.customValue', this.Student.selectedClass.metadata.customValue)
        this.Student.metadata = JSON.parse(JSON.stringify(this.Student.selectedClass.metadata.customValue));
      } else {
        this.Student.metadata = null
      }
      // console.log(' this.Student.metadata', this.Student.metadata)
    },
    OpenStudentDialog() {
      console.log('this Student', this.Student)
      this.dialog = !this.dialog
    },
    getAge(dateString) {
      var today = new Date();
      var birthDate = new Date(dateString);
      var age = today.getFullYear() - birthDate.getFullYear();
      var m = today.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      return age;
    },
    adultNameDone() {
      if (this.leadType === 1) {
        this.Lead.fatherName = `${this.Student.firstName} ${this.Student.lastName}`
      }
      this.$v.Lead.$touch();
      this.$v.Student.$touch();
      if (this.$v.Lead.$invalid || this.$v.Student.$invalid) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: this.$t("toastMessage.text.invalidFields"),
          });
      } else {
        this.Student.ClassId = this.Student.selectedClass.id
        this.adultStep = 2;
      }
    },
    adultAddressDone() {
      this.$v.Address.$touch();
      if (this.$v.Address.$invalid) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: this.$t("toastMessage.text.invalidFields"),
          });
      } else {
        this.submitWithOutCard();
      }
    },
    nameDone() {
      this.$v.Lead.$touch();
      if (this.$v.Lead.$invalid) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: this.$t("toastMessage.text.invalidFields"),
          });
      } else {
        this.step = 4;
      }
    },
    addressDone() {
      this.$v.Address.$touch();
      if (this.$v.Address.$invalid) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: this.$t("toastMessage.text.invalidFields"),
          });
      } else {
        this.step = 3;
      }
    },
    emergencyDone() {
      this.$v.Emergency.$touch();
      if (this.$v.Emergency.$invalid) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: this.$t("toastMessage.text.invalidFields"),
          });
      } else {
        this.submitWithOutCard();
      }
    },
    studentsDone() {
      if (this.hasStudentDraft && !this.addStudent()) return;
      if (this.Students.length === 0) {
        this.$v.Student.$touch();
        this.$swal.mixin({
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
        }).fire({ icon: "error", title: "Please add at least one student" });
        return;
      }
      this.step = 2;
    },
    studentDone() {
      if (this.Students.length === 0) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: "Please add student",
          });
      } else {
        this.step = 5;
      }
    },
    handleAnimation(anim) {
      this.anim = anim;
    },
    closeDialog() {
      this.emptyDialog();
      this.$v.Student.$reset();
      this.dialog = false;
    },
    emptyFrom() {
      this.Lead = {
        fatherName: null,
        fatherPhoneNumber: null,
        fatherEmailAddress: null,
        motherName: null,
        motherPhoneNumber: null,
        motherEmailAddress: null,
      },
        this.Address = {
          address: null,
          city: null,
          state: "NY",
          zip: null,
        },
        this.Emergency = {
          emergencyName: null,
          emergencyPhoneNumber: null,
          emergencyRelation: null,
        },
        this.Student = {
          firstName: null,
          lastName: null,
          dob: null,
          selectedClass: null,
          ClassId: null,
        }
    },
    emptyDialog() {
      this.Student = {
        firstName: null,
        lastName: null,
        dob: null,
        selectedClass: null,
        metadata: null
      };
    },
    fixFatherEmail() {
      this.Lead.fatherEmailAddress = this.Lead.fatherEmailAddress
        .toLowerCase()
        .trim();
    },
    fixMotherEmail() {
      this.Lead.motherEmailAddress = this.Lead.motherEmailAddress
        .toLowerCase()
        .trim();
    },
    loadBusiness() {
      let id = this.$route.params.id;
      console.log("id", id);
      applicationService
        .getBusinessInformation({ key: id })
        .then((response) => {
          console.log("response", response);
          this.Business = response.data;
          this.StripeKey = this.Business.metadata.publicStripeId;
          this.Classes = this.Business.Classes;
        })
        .catch((error) => {
          console.log("error", error);
          this.$router.push({ name: "pagenotfound" });
        });
    },
    addStudent() {
      this.$v.Student.$touch();
      if (this.$v.Student.$invalid || (this.Student.dob && this.getAge(this.Student.dob) < 5)) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: this.Student.dob && this.getAge(this.Student.dob) < 5
              ? "Minimum admission age is 5 years"
              : this.$t("toastMessage.text.invalidFields"),
          });
        return false;
      } else {
        let haveError = false
        console.log('this.Student.metadata', this.Student.metadata)
        if (this.Student.metadata) {
          this.Student.metadata.map(row => {
            console.log('row isRequired', row.isRequired)
            if (row.isRequired) {
              if (row.selectedAnswerType === 'Short answer') {
                if (!row.answer) {
                  this.$swal
                    .mixin({
                      toast: true,
                      position: "top-end",
                      showConfirmButton: false,
                      timer: 3000,
                    })
                    .fire({
                      icon: "error",
                      title: `Missing - ${row.question}`,
                    });
                  haveError = true
                }
              }
              if (row.selectedAnswerType === 'Paragraph') {
                if (!row.answer) {
                  this.$swal
                    .mixin({
                      toast: true,
                      position: "top-end",
                      showConfirmButton: false,
                      timer: 3000,
                    })
                    .fire({
                      icon: "error",
                      title: `Missing - ${row.question}`,
                    });
                  haveError = true
                }
              }
              if (row.selectedAnswerType === 'Multiple choice') {
                if (row.answer == null || row.answer === '') {
                  this.$swal
                    .mixin({
                      toast: true,
                      position: "top-end",
                      showConfirmButton: false,
                      timer: 3000,
                    })
                    .fire({
                      icon: "error",
                      title: `Missing - ${row.question}`,
                    });
                  haveError = true
                }

              }
              if (row.selectedAnswerType === 'Dropdown') {
                if (!row.answer) {
                  this.$swal
                    .mixin({
                      toast: true,
                      position: "top-end",
                      showConfirmButton: false,
                      timer: 3000,
                    })
                    .fire({
                      icon: "error",
                      title: `Missing - ${row.question}`,
                    });
                  haveError = true
                }
              }
            }
          })
        }

        if (haveError) {
          return false
        } else {
          console.log('adding student', this.Student)
          this.Student.ClassId = this.Student.selectedClass.id
          this.Students.push(this.Student);
          this.closeDialog();
          return true;
        }

      }
    },
    deleteItem(item) {
      this.Students.splice(this.Students.indexOf(item), 1);
      // this.editedIndex = this.Students.indexOf(item)
      // this.editedItem = Object.assign({}, item)
      // this.dialogDelete = true
    },
    submitWithOutCard() {
      if (this.leadType === 0) {
        if (this.Students.length === 0) {
          this.$swal
            .mixin({
              toast: true,
              position: "top-end",
              showConfirmButton: false,
              timer: 3000,
            })
            .fire({
              icon: "error",
              title: "Please add student",
            });
        } else {
          this.submit()
        }
      } else {
        this.submit()
      }

    },
    async submit() {
      this.$v.Lead.$touch();
      if (this.$v.Lead.$invalid) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: this.$t("toastMessage.text.invalidFields"),
          });
      } else {
        // if (this.leadType === 0) {
        //   if (this.Students.length === 0) {
        //     this.$swal
        //       .mixin({
        //         toast: true,
        //         position: "top-end",
        //         showConfirmButton: false,
        //         timer: 4000,
        //       })
        //       .fire({
        //         icon: "error",
        //         title: "Please Enter Student",
        //       });
        //     this.loading = false;
        //     this.dialog = true;
        //   }
        // }

        if (this.leadType === 0 && this.Students.length === 0) {
          this.$swal.fire({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 4000,
            icon: "error",
            title: "Please Enter Student"
          });
          this.loading = false;
          this.dialog = true;
        }

        try {
          // this.loading = true;
          //  this.Lead.students = this.Students;
          //   this.Lead = {
          //     ...this.Lead,
          //     ...this.Emergency,
          //     ...this.Address,
          //   }
          //   console.log('lead', this.Lead)
          this.Lead.students = this.Students;
          this.Lead.status = "Open"
          this.Lead = {
            ...this.Lead,
            ...this.Emergency,
            ...this.Address,
          }

          if (this.leadType === 0) {
            this.Lead.isChild = true
          } else {
            this.Lead.isChild = false
            this.Lead.students.push(this.Student)
          }

          if (this.Lead.fatherPhoneNumber) {
            this.Lead.fatherPhoneNumber = this.Lead.fatherPhoneNumber.replace(
              /[^a-zA-Z0-9]/g,
              ""
            );
          }

          if (this.Lead.motherPhoneNumber) {
            this.Lead.motherPhoneNumber = this.Lead.motherPhoneNumber.replace(
              /[^a-zA-Z0-9]/g,
              ""
            );
          }

          if (this.Lead.emergencyPhoneNumber) {
            this.Lead.emergencyPhoneNumber =
              this.Lead.emergencyPhoneNumber.replace(/[^a-zA-Z0-9]/g, "");
          }

          console.log("this.Students.length", this.currentBusiness, this.Lead);
          this.loading = true;
          applicationService
            .publicLeadCreate(this.currentBusiness.alias, this.Lead)
            .then((response) => {
              this.submittedApplicationNumber = response && response.data
                ? response.data.localId
                : null;
              this.infoCollected = true;
              this.loading = false;
            })
            .catch((err) => {
              this.loading = false;
              console.log("err", err);
              this.$swal
                .mixin({
                  toast: true,
                  position: "top-end",
                  showConfirmButton: false,
                  timer: 3000,
                })
                .fire({
                  icon: "error",
                  title: err.message,
                });
            });

        } catch (error) {
          this.loading = false;
          console.log("error adding card", error);
          this.$swal
            .mixin({
              toast: true,
              position: "top-end",
              showConfirmButton: false,
              timer: 3000,
            })
            .fire({
              icon: "error",
              title: error.message,
            });
        }
      }
    },
  },
};
</script>

<style scoped>
.public-application-form {
  color: #2b3830;
}

.public-application-form__notice,
.application-success {
  padding: 36px 24px;
  border: 1px solid #e1e8e2;
  border-radius: 18px;
  text-align: center;
  background: #fff;
  box-shadow: 0 14px 35px rgba(42, 72, 50, .08);
}

.public-application-form__notice h2 {
  margin: 12px 0 4px;
  color: #29352e;
  font-size: 20px;
}

.public-application-form__notice p {
  margin: 0;
  color: #748078;
  font-size: 13px;
}

.application-type-picker,
.application-flow__card,
.application-success {
  overflow: hidden;
  border: 1px solid #dfe8e1;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 14px 35px rgba(42, 72, 50, .08);
}

.application-type-picker {
  padding: 28px;
}

.application-type-picker__heading {
  display: flex;
  gap: 14px;
  align-items: center;
}

.application-type-picker__icon,
.application-type-option__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #edf7ef;
}

.application-type-picker__icon {
  width: 48px;
  height: 48px;
  flex: 0 0 48px;
  border-radius: 13px;
}

.application-type-picker__heading span,
.application-flow__intro span,
.application-student-dialog__header span,
.application-success__copy > span {
  color: #2f7d4a;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: .09em;
  text-transform: uppercase;
}

.application-type-picker__heading h2,
.application-flow__intro h2,
.application-student-dialog__header h2,
.application-success__copy h2 {
  margin: 2px 0;
  color: #243128;
  font-size: 20px;
  line-height: 1.25;
}

.application-type-picker__heading p,
.application-flow__intro p,
.application-student-dialog__header p,
.application-success__copy p {
  margin: 0;
  color: #748078;
  font-size: 12px;
}

.application-type-picker__options {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-top: 24px;
}

.application-type-option {
  display: flex;
  min-height: 104px;
  padding: 16px;
  border: 1px solid #dfe7e1;
  border-radius: 13px;
  align-items: center;
  gap: 13px;
  color: inherit;
  background: #fff;
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: border-color .18s ease, box-shadow .18s ease, transform .18s ease;
}

.application-type-option:hover,
.application-type-option:focus-visible,
.application-type-option--selected {
  border-color: #79ad87;
  outline: none;
  box-shadow: 0 8px 20px rgba(47, 125, 74, .11);
  transform: translateY(-1px);
}

.application-type-option__icon {
  width: 48px;
  height: 48px;
  flex: 0 0 48px;
  border-radius: 12px;
}

.application-type-option__copy {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
}

.application-type-option__copy strong {
  color: #2b3830;
  font-size: 14px;
}

.application-type-option__copy small {
  margin-top: 3px;
  color: #78847c;
  font-size: 10px;
  line-height: 1.45;
}

.application-type-picker__privacy {
  display: flex;
  gap: 7px;
  margin-top: 18px;
  align-items: center;
  justify-content: center;
  color: #758078;
  font-size: 10px;
}

.application-flow {
  max-width: 100%;
}

.application-flow__card {
  width: 100%;
}

.application-flow__intro {
  display: flex;
  padding: 21px 24px;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #e6ece7;
  background: linear-gradient(135deg, #f9fcfa 0%, #eff7f1 100%);
}

.application-flow__step-count {
  flex: 0 0 auto;
  padding: 7px 10px;
  border-radius: 9px;
  color: #2f7d4a;
  background: #e4f2e7;
  font-size: 10px;
  font-weight: 800;
}

.application-stepper {
  border-radius: 0;
}

.application-stepper ::v-deep .v-stepper__step {
  min-height: 60px;
  padding: 18px 24px;
  color: #425047;
  font-size: 13px;
  font-weight: 700;
}

.application-stepper ::v-deep .v-stepper__step__step {
  width: 27px;
  height: 27px;
  font-size: 11px;
}

.application-stepper ::v-deep .v-stepper__content {
  box-sizing: border-box;
  max-width: calc(100% - 61px);
  margin: -8px 24px -8px 37px;
  padding: 10px 20px 22px 30px;
}

.application-section-help {
  margin: 0 0 10px;
  color: #6f7d74;
  font-size: 12px;
}

.application-student-editor {
  padding: 16px;
  border: 1px solid #e1e9e3;
  border-radius: 12px;
  background: #f9fbf9;
}

.application-student-editor__heading {
  display: flex;
  margin-bottom: 6px;
  flex-direction: column;
}

.application-student-editor__heading strong {
  color: #2b3830;
  font-size: 14px;
}

.application-student-editor__heading span,
.application-student-editor__age {
  color: #748078;
  font-size: 11px;
}

.application-student-questions {
  display: grid;
  gap: 12px;
  margin-top: 12px;
}

.application-student-questions__item {
  padding: 12px;
  border: 1px solid #e1e8e2;
  border-radius: 9px;
  background: #fff;
}

.application-student-questions__item label {
  display: block;
  margin-bottom: 8px;
  color: #344239;
  font-size: 12px;
  font-weight: 700;
}

.application-student-list {
  display: grid;
  gap: 8px;
  margin-bottom: 12px;
}

.application-student-list__item {
  display: flex;
  min-width: 0;
  padding: 9px 10px 9px 13px;
  border: 1px solid #dce8df;
  border-radius: 10px;
  align-items: center;
  justify-content: space-between;
  background: #f2f8f3;
}

.application-student-list__item div {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.application-student-list__item span {
  overflow: hidden;
  color: #718078;
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.application-stepper ::v-deep .v-card__text {
  padding: 4px 0 14px;
}

.application-stepper ::v-deep .v-text-field--outlined .v-input__slot,
.application-student-dialog ::v-deep .v-text-field--outlined .v-input__slot {
  border-radius: 9px;
}

.application-stepper ::v-deep .v-card__actions {
  padding: 14px 0 0;
  border-top: 1px solid #edf1ee;
}

.application-stepper ::v-deep .v-btn,
.application-student-dialog__actions .v-btn {
  min-height: 42px;
  border-radius: 9px;
  text-transform: none;
  letter-spacing: 0;
}

.application-stepper ::v-deep .v-data-table {
  overflow: hidden;
  border: 1px solid #e4eae5;
  border-radius: 10px;
}

.application-success {
  max-width: 620px;
  margin: 0 auto;
}

.application-success__copy {
  max-width: 470px;
  margin: -16px auto 0;
}

.application-success__copy h2 {
  font-size: 24px;
}

.application-success__copy p {
  margin-top: 8px;
  line-height: 1.6;
}

.application-success__number {
  display: inline-flex;
  margin-top: 18px;
  padding: 10px 18px;
  border-radius: 11px;
  flex-direction: column;
  color: #2f7d4a;
  background: #edf7ef;
}

.application-success__number span {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
}

.application-success__number strong {
  font-size: 22px;
}

.application-success__contact {
  font-size: 11px;
}

.application-student-dialog {
  overflow: hidden;
  border-radius: 16px !important;
}

.application-student-dialog__header {
  display: flex;
  min-height: 82px;
  padding: 16px 20px;
  align-items: center;
  justify-content: space-between;
  background: linear-gradient(135deg, #f9fcfa 0%, #edf7ef 100%);
}

.application-student-dialog__body {
  padding: 20px 20px 8px !important;
  background: #f8faf8;
}

.application-student-dialog__age {
  padding: 10px 20px !important;
  color: #68756d;
  font-size: 11px;
  text-align: center;
}

.application-student-dialog__actions {
  padding: 13px 20px !important;
}

.application-footer {
  display: flex;
  gap: 5px;
  padding: 22px 8px 4px;
  align-items: center;
  justify-content: center;
  color: #879188;
  font-size: 9px;
}

.application-footer strong {
  color: #5f6d63;
}

.application-footer span {
  margin-left: 5px;
  color: #a0aaa2;
}

.centerdiv {
  position: relative;
  margin: auto;
}

@media (max-width: 599px) {
  .application-stepper ::v-deep .v-stepper__content {
    max-width: calc(100% - 37px);
    margin-right: 8px;
    margin-left: 19px;
    padding-right: 6px;
    padding-left: 18px;
  }

  .application-student-editor {
    padding: 12px;
  }
  .application-type-picker,
  .application-flow__card,
  .application-success {
    border-radius: 15px;
  }

  .application-type-picker {
    padding: 20px 15px;
  }

  .application-type-picker__heading {
    align-items: flex-start;
  }

  .application-type-picker__heading h2,
  .application-flow__intro h2 {
    font-size: 18px;
  }

  .application-type-picker__options {
    grid-template-columns: 1fr;
    margin-top: 18px;
  }

  .application-type-option {
    min-height: 92px;
    padding: 13px;
  }

  .application-type-picker__privacy {
    align-items: flex-start;
    text-align: center;
  }

  .application-flow__intro {
    padding: 16px 14px;
    align-items: flex-start;
  }

  .application-flow__intro p {
    display: none;
  }

  .application-flow__step-count {
    margin-top: 2px;
  }

  .application-stepper ::v-deep .v-stepper__step {
    min-height: 56px;
    padding: 16px 14px;
  }

  .application-stepper ::v-deep .v-stepper__content {
    margin: -8px 14px -8px 27px;
    padding: 8px 0 20px 19px;
  }

  .application-stepper ::v-deep .v-card__actions {
    gap: 8px;
  }

  .application-stepper ::v-deep .v-card__actions .v-btn {
    min-width: 92px;
    flex: 1;
    margin: 0;
  }

  .application-stepper ::v-deep .v-data-table__wrapper {
    overflow-x: auto;
  }

  .application-success {
    padding: 24px 12px 30px;
  }

  .application-success__copy h2 {
    font-size: 21px;
  }

  .application-student-dialog__header {
    min-height: 76px;
    padding: 14px;
  }

  .application-student-dialog__header p {
    display: none;
  }

  .application-student-dialog__body {
    padding: 14px 14px 4px !important;
  }

  .application-student-dialog__actions {
    padding: 12px 14px !important;
  }

  .application-footer {
    flex-wrap: wrap;
  }
}
</style>
